import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	QueryList,
	Signal,
	signal,
	ViewChild,
	ViewChildren,
	WritableSignal,
	inject,
} from '@angular/core'
import { DatePipe, NgTemplateOutlet } from '@angular/common'
import { Subject, takeUntil } from 'rxjs'
import { TuiExpand, TuiIcon } from '@taiga-ui/core'
import { TuiSkeleton } from '@taiga-ui/kit'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { OrderHints } from '@modules/orders-module/constants/order-hints'
import { OrderClassEnum } from '@modules/orders-module/enums/order-class.enum'
import { OrderStatusEnum } from '@modules/orders-module/enums/order-status.enum'
import { OrderHintsInterface } from '@modules/stocks-module/orders/interfaces/order-hints.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { OrderInfoBodyPreviewComponent } from './common/compact-order-accordion/order-info-body-preview.component'
import { OrderInfoBodyDetailedComponent } from './common/order-info-body-detailed/order-info-body-detailed.component'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { HttpErrorResponse } from '@angular/common/http'
import { CompanyLogoComponent } from '@shared/components/common/company-logo/company-logo.component'
import { OpenOrderStatuses } from '@modules/orders-module/constants/open-order-statuses'
import { FilledOrderStatuses } from '@modules/orders-module/constants/filled-order-statuses'
import { CancelOrderStatuses } from '@modules/orders-module/constants/cancel-order-statuses'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { AlertService } from '@shared/services/alert.service'
import { AssetExanteInterface } from '@modules/stocks-module/general/interfaces/asset-exante.interface'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-order-info-card',
	templateUrl: './order-info-card.component.html',
	styleUrl: './order-info-card.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		OrderInfoBodyDetailedComponent,
		OrderInfoBodyPreviewComponent,
		TuiSkeleton,
		DatePipe,
		TranslatePipe,
		HighlightDirective,
		NgTemplateOutlet,
		TuiExpand,
		CompanyLogoComponent,
	],
})
export class OrderInfoCardComponent implements OnInit, OnDestroy, AfterViewInit {
	protected readonly math: Math = Math
	protected readonly array: ArrayConstructor = Array
	protected readonly orderClassEnum: typeof OrderClassEnum = OrderClassEnum
	protected readonly orderHints: OrderHintsInterface[] = OrderHints
	protected readonly orderStatusEnum: typeof OrderStatusEnum = OrderStatusEnum
	protected readonly openOrderStatuses: typeof OpenOrderStatuses = OpenOrderStatuses
	protected readonly filledOrderStatuses: typeof FilledOrderStatuses = FilledOrderStatuses
	protected readonly cancelOrderStatuses: typeof CancelOrderStatuses = CancelOrderStatuses

	public order: InputSignal<OrderInterface | null> = input<OrderInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	@ViewChild('orderWrap') orderWrap: ElementRef<HTMLElement> | null = null
	@ViewChild('connector', { read: ElementRef }) connector: ElementRef<HTMLElement> | null = null
	@ViewChild('ocoConnector', { read: ElementRef }) ocoConnector: ElementRef<HTMLElement> | null = null
	@ViewChildren('connectorArrow', { read: ElementRef }) arrowIcons: QueryList<ElementRef<HTMLElement>> | null = null
	@ViewChildren('accordionHeader', { read: ElementRef }) accordionHeaders: QueryList<ElementRef<HTMLElement>> | null =
		null

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)

	private _resizeObserver: ResizeObserver | null = null

	protected assetsInfo: WritableSignal<AssetExanteInterface | null> = signal<AssetExanteInterface | null>(null)
	protected accordionExpandedHeaders: WritableSignal<boolean[]> = signal<boolean[]>([])
	protected isLoadingAssetInfo: WritableSignal<boolean> = signal<boolean>(false)
	protected isUpdatingConnectors: WritableSignal<boolean> = signal<boolean>(false)

	protected isAnyAccordionExpanded: Signal<boolean> = computed((): boolean =>
		this.accordionExpandedHeaders().some((flag: boolean): boolean => flag),
	)

	protected isOCO: Signal<boolean> = computed((): boolean => this.order()?.order_class === this.orderClassEnum.OCO)

	protected isComplexOrder: Signal<boolean> = computed((): boolean => {
		const order: OrderInterface | null = this.order()
		if (!order) return false

		return this._ordersStocksHelperService.checkIsComplexOrder(order) && !!order.children_order?.length
	})
	private _getAssetsInfo(): void {
		const order: OrderInterface | null = this.order()
		if (!order) return

		this.isLoadingAssetInfo.set(true)

		this._stocksService
			.getExanteAsset(order.symbol)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(assets: AssetExanteInterface): void => {
					this.assetsInfo.set(assets)
					this.isLoadingAssetInfo.set(false)
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('orders.assetsInfoFailed'),
					)
					this.isLoadingAssetInfo.set(false)
				},
			)
	}

	private _initAccordions(): void {
		const parent: number = 1
		const length: number = (this.order()?.children_order?.length ?? 0) + parent

		this.accordionExpandedHeaders.set(new this.array<boolean>(length).fill(false))
	}

	private _updateAllConnectors(): void {
		this.isUpdatingConnectors.set(true)

		requestAnimationFrame((): void => {
			this.updateOCOConnector()
			this.updateBracketAndOTOConnector()
			this.isUpdatingConnectors.set(false)
		})
	}

	private _initResizeObserver(): void {
		if (!this.orderWrap?.nativeElement) return

		this._resizeObserver = new ResizeObserver((): void => this._updateAllConnectors())
		this._resizeObserver.observe(this.orderWrap.nativeElement)
	}

	protected getOrderHints(
		order: OrderInterface | null,
		isMain: boolean,
		childOrderIndex?: number,
	): OrderHintsInterface | null {
		if (!order) return null

		if (isMain) {
			return (
				this.orderHints.find(
					(hints: OrderHintsInterface): boolean => hints.isMain && hints.orderClass === order.order_class,
				) ?? null
			)
		}

		if (childOrderIndex === undefined) return null

		const foundOrderHints: OrderHintsInterface[] = this.orderHints.filter(
			(hints: OrderHintsInterface): boolean => !hints.isMain && hints.orderClass === order.order_class,
		)

		return foundOrderHints[childOrderIndex] ?? null
	}

	protected updateBracketAndOTOConnector(): void {
		const wrapperEl: HTMLElement | undefined = this.orderWrap?.nativeElement
		if (!wrapperEl || !this.accordionHeaders || !this.accordionHeaders.length) return

		const headerEls: HTMLElement[] = this.accordionHeaders.map(
			(el: ElementRef<HTMLElement>): HTMLElement => el.nativeElement,
		)
		const wrapperRectTop: number = wrapperEl.getBoundingClientRect().top
		const mainHeaderRect: DOMRect | undefined = headerEls[0]?.getBoundingClientRect()
		const childRects: DOMRect[] = headerEls
			.slice(1)
			.map((element: HTMLElement): DOMRect => element.getBoundingClientRect())
		if (!headerEls.length) return

		const centers: number[] = childRects.map((rect: DOMRect): number => rect.top + rect.height / 2 - wrapperRectTop)
		const connectorEl: HTMLElement | undefined = this.connector?.nativeElement
		if (!connectorEl) return

		const connectorTop: number = (mainHeaderRect?.bottom ?? 0) - wrapperRectTop
		const lastCenter: number = centers[centers.length - 1] ?? 0
		const connectorHeight: number = this.math.max(0, lastCenter - connectorTop)

		connectorEl.style.top = `${this.math.round(connectorTop)}px`
		connectorEl.style.height = `${this.math.round(connectorHeight)}px`

		this.arrowIcons?.forEach((icon: ElementRef<HTMLElement>, index: number): void => {
			const arrowEl: HTMLElement = icon.nativeElement
			const arrowRect: DOMRect = arrowEl.getBoundingClientRect()
			const centerY: number = centers[index] ?? 0
			arrowEl.style.top = `${this.math.round(centerY - connectorTop - arrowRect.height / 2)}px`
		})
	}

	protected updateOCOConnector(): void {
		const wrapperEl: HTMLElement | undefined = this.orderWrap?.nativeElement
		if (!wrapperEl || !this.accordionHeaders || !this.accordionHeaders.length) return

		const headerEls: HTMLElement[] = this.accordionHeaders.map(
			(el: ElementRef<HTMLElement>): HTMLElement => el.nativeElement,
		)
		const mainHeaderRect: DOMRect | undefined = headerEls[0]?.getBoundingClientRect()
		if (!mainHeaderRect || !headerEls.length) return

		const ocoConnectorEl: HTMLElement | undefined = this.ocoConnector?.nativeElement
		if (!ocoConnectorEl) return

		const childrenEl: HTMLElement[] = headerEls.slice(1)
		const wrapperRect: DOMRect = wrapperEl.getBoundingClientRect()

		const childCenterEls: number[] = childrenEl.map((rect: HTMLElement): number => {
			const r: DOMRect = rect.getBoundingClientRect()
			return r.top + r.height / 2 - wrapperRect.top
		})

		const mainCenter: number = mainHeaderRect.top + mainHeaderRect.height / 2 - wrapperRect.top
		const connectorTop: number = this.math.min(mainCenter, ...childCenterEls)
		const connectorBottom: number = this.math.max(mainCenter, ...childCenterEls)
		const connectorHeight: number = this.math.max(0, connectorBottom - connectorTop)

		ocoConnectorEl.style.top = `${this.math.round(connectorTop)}px`
		ocoConnectorEl.style.height = `${this.math.round(connectorHeight)}px`
	}

	protected toggleAccordion(headerIndex: number): void {
		if (!this.isComplexOrder) return

		this.accordionExpandedHeaders.update((flags: boolean[]): boolean[] =>
			flags.map((flag: boolean, flagIndex: number): boolean => flagIndex === headerIndex && !flag),
		)
	}

	protected hasStatus(order: OrderInterface | undefined, statuses: OrderStatusEnum[]): boolean {
		if (!order) return false
		return statuses.includes(order.status)
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._initAccordions()
		this._getAssetsInfo()
	}

	ngAfterViewInit(): void {
		this._initResizeObserver()
	}

	ngOnDestroy(): void {
		this._resizeObserver?.disconnect()
		this._destroy$.next()
		this._destroy$.complete()
	}
}
